import { createStore } from 'vuex';
import ApiClient from '../core/ApiClient.js';
import {formModule} from './modules/formModule';
import {currencyModule} from './modules/currencyModule';
import { pages } from './modules/pages';
import {
    activateSecondHomeFunnel,
    isSecondHomeFunnelActive,
} from '../utils/secondHomeFunnel.js';
import { shouldHideCurrencyConverter, forcedCurrencyCodeForLang } from '../utils/hideCurrencyConverter.js';
import { resolveCurrencyCodeByLanguage } from '../utils/languageCurrency.js';
import { normalizeCmsAssetUrl } from '../utils/cmsAssetUrl.js';

function withNormalizedFlag(row) {
    if (!row || typeof row !== 'object') return row;
    if (!row.flag) return row;
    return { ...row, flag: normalizeCmsAssetUrl(row.flag) || row.flag || '' };
}

function normalizeLanguagesList(languages) {
    return Array.isArray(languages) ? languages.map(withNormalizedFlag) : [];
}

function normalizeCountriesList(countries) {
    return Array.isArray(countries) ? countries.map(withNormalizedFlag) : [];
}

function normalizeLanguagesApiResponse(res) {
    const body = res?.data;
    if (Array.isArray(body?.data)) return body.data;
    if (Array.isArray(body)) return body;
    return [];
}

/** Single in-flight request: concurrent `dispatch('loadLanguages')` share one promise. */
let loadLanguagesInflight = null;

function syncLangIdToLocalStorage(lang) {
    if (typeof localStorage === 'undefined' || lang == null || lang.id == null) return;
    localStorage.setItem('langId', String(lang.id));
}

export default createStore({
    modules: {
        form: formModule,
        currency: currencyModule,
        pages
    },
    state: {
        project: null,
        page: null,
        texts: {},
        lang: null,
        languages: [],
        currencies: [],
        defPath: '/',
        mobileView: false,
        euroOnlyCurrency: false,
        secondHomeFunnel: false,
    },
    getters: {
        project: state => state.project,
        page: state => state.page,
        texts: state => state.texts,
        lang: state => state.lang,
        languages: state => state.languages,
        currencies: state => state.currencies,
        currency: (state, getters) => getters['currency/currentCurrency'],
        defPath: state => state.defPath,
        mobileView: state => state.mobileView,
        euroOnlyCurrency: state => state.euroOnlyCurrency,
        secondHomeFunnel: state => state.secondHomeFunnel,
        shouldLockEuro: state => state.euroOnlyCurrency || state.secondHomeFunnel,
        hideCurrencyConverter: state => shouldHideCurrencyConverter(state.lang),
        showCurrencyConverter: (state, getters) => !getters.shouldLockEuro && !getters.hideCurrencyConverter,
    },
    mutations: {
        SET_PROJECT(state, project) {
            state.project = project;
        },
        SET_PAGE(state, page) {
            state.page = page;
        },
        SET_TEXTS(state, texts) {
            state.texts = texts;
        },
        SET_LANG(state, lang) {
            state.lang = lang ? withNormalizedFlag(lang) : lang;
        },
        SET_LANGUAGES(state, languages) {
            state.languages = normalizeLanguagesList(languages);
        },
        SET_CURRENCIES(state, currencies) {
            state.currencies = currencies;
        },
        SET_DEF_PATH(state, defPath) {
            state.defPath = defPath;
        },
        INIT_STATE(state, data) {
            state.project = data.project || null;
            state.page = data.page || null;
            state.texts = data.texts || {};
            state.lang = data.lang ? withNormalizedFlag(data.lang) : null;
            state.languages = normalizeLanguagesList(data.languages);
            state.currencies = data.currencies || [];
            state.defPath = data.defPath || '/';
            state.mobileView = data.mobileView || false;
            state.euroOnlyCurrency = !!data.euroOnlyCurrency;
            state.secondHomeFunnel = !!data.secondHomeFunnel;
        },
        SET_SECOND_HOME_FUNNEL(state, active) {
            state.secondHomeFunnel = !!active;
        },
    },
    actions: {
        initState({ commit, dispatch }, data) {
            commit('INIT_STATE', data);
            syncLangIdToLocalStorage(data?.lang);
            if (Array.isArray(data?.countries) && data.countries.length > 0) {
                commit('form/SET_COUNTRIES', normalizeCountriesList(data.countries), { root: true });
                commit(
                    'form/SET_COUNTRIES_FETCH_LANGUAGE_ID',
                    data?.lang?.id ?? null,
                    { root: true }
                );
            }
            if (
                Array.isArray(data?.categorizedProducts)
                && data.categorizedProducts.length > 0
            ) {
                commit('form/SET_CATEGORIZED_PRODUCTS', data.categorizedProducts, { root: true });
            }
            dispatch('syncSecondHomeFunnel', data);
            if (Array.isArray(data?.countries) && data.countries.length > 0) {
                dispatch('form/loadSavedData', null, { root: true })
                    .then(() => dispatch('form/alignPlateCountryWithSiteLanguage', null, { root: true }))
                    .catch(() => {});
            }
        },

        syncSecondHomeFunnel({ commit, dispatch }, data = {}) {
            if (data?.euroOnlyCurrency) {
                activateSecondHomeFunnel();
                commit('SET_SECOND_HOME_FUNNEL', true);
                dispatch('currency/lockToEuro', null, { root: true });
                return;
            }

            const inFunnel = isSecondHomeFunnelActive() || !!data?.secondHomeFunnel;

            if (inFunnel) {
                activateSecondHomeFunnel();
                commit('SET_SECOND_HOME_FUNNEL', true);
                dispatch('currency/lockToEuro', null, { root: true });
                return;
            }

            commit('SET_SECOND_HOME_FUNNEL', false);
            dispatch('currency/initCurrency', null, { root: true });
            dispatch('syncCurrencyToLanguage', { skipCatalogRefresh: true });
        },

        /**
         * Reads SSR-rendered bootstrap from `window.__APP_DATA__` (see
         * vendor/armspider/client-app/resources/views/index.blade.php) and seeds
         * the store. Replaces the inline bootstrap that used to live in app.js,
         * so languages/countries/currencies arrive without extra API requests.
         */
        bootstrapFromWindow({ dispatch }) {
            if (typeof window === 'undefined') return null;
            const data = window.__APP_DATA__;
            if (!data) return null;

            dispatch('initState', {
                project: data.project,
                page: data.page,
                texts: data.texts,
                lang: data.lang,
                languages: data.languages,
                countries: data.countries,
                currency: data.currency,
                currencies: data.currencies,
                categorizedProducts: data.categorizedProducts,
                defPath: data.defPath,
                mobileView: data.mobileView,
                euroOnlyCurrency: !!data.euroOnlyCurrency,
                secondHomeFunnel: !!data.secondHomeFunnel,
            });

            return data;
        },

        /**
         * Languages list. Resolves with the bootstrap-seeded list when the SSR
         * payload already provided one (no /languages API call needed); only
         * pages without bootstrap (or `force: true`) hit the API.
         */
        loadLanguages({ commit, state }, options = {}) {
            const force = options && options.force === true;
            if (!force && Array.isArray(state.languages) && state.languages.length > 0) {
                return Promise.resolve(state.languages);
            }
            if (loadLanguagesInflight) {
                return loadLanguagesInflight;
            }
            loadLanguagesInflight = (async () => {
                try {
                    const api = new ApiClient();
                    const res = await api.request({ path: '/languages' });
                    const list = normalizeLanguagesApiResponse(res);
                    commit('SET_LANGUAGES', Array.isArray(list) ? list : []);
                    return Array.isArray(list) ? list : [];
                } catch {
                    commit('SET_LANGUAGES', []);
                    return [];
                }
            })().finally(() => {
                loadLanguagesInflight = null;
            });

            return loadLanguagesInflight;
        },

        changeLanguage({ commit, dispatch }, lang) {
            commit('SET_LANG', lang);
            syncLangIdToLocalStorage(lang);
            dispatch('syncCurrencyToLanguage', { skipCatalogRefresh: true });
            dispatch('form/syncVehicleCountryToLanguage', { force: true }, { root: true }).catch(() => {});
            dispatch('form/reloadCountries', null, { root: true }).catch(() => {});
            dispatch('form/reloadProductsCatalog', null, { root: true }).catch(() => {});
        },

        syncCurrencyToLanguage({ state, dispatch }, options = {}) {
            const skipCatalogRefresh = !!options?.skipCatalogRefresh;
            const currencyPayload = (code) => (
                skipCatalogRefresh ? { code, skipCatalogRefresh: true } : code
            );

            if (state.euroOnlyCurrency || state.secondHomeFunnel) {
                dispatch('currency/lockToEuro', null, { root: true });
                return;
            }
            if (shouldHideCurrencyConverter(state.lang)) {
                dispatch('currency/unlockEuroLock', null, { root: true });
                dispatch(
                    'currency/setCurrencyByCode',
                    currencyPayload(forcedCurrencyCodeForLang(state.lang)),
                    { root: true }
                );
                return;
            }
            dispatch('currency/unlockEuroLock', null, { root: true });
            const currencyCode = resolveCurrencyCodeByLanguage(
                state.lang,
                state.languages
            );
            dispatch('currency/setCurrencyByCode', currencyPayload(currencyCode), { root: true });
        }
    }
});

